import { type ColorToken, GenericTable } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { AddIconChip } from '@backrs_ui/grow-ui/src/components/MyColleges/AddIconChip';
import { CollegeChip } from '@backrs_ui/grow-ui/src/components/MyColleges/CollegeChip';
import { EstimatedAmount } from '@backrs_ui/grow-ui/src/pages/MyColleges/components/NetCostText';
import {
  COMPARE_FACTORS,
  CompareFactor,
  ComparedCollege,
  ScoredComparison,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { Box, SxProps, TableCell, TableRow, Theme, Typography } from '@mui/material';

import { BestForWatch, TopMatchBadge } from './CompareBadges';
import { WeightBadge } from './WeightBadge';
import { factFor, factorLabel, fitFill, isEstimate, percent, topMatchId } from '../compare.util';

const STRINGS = appStrings.modules.myColleges.compare;

type CompareHeatMapProps = Readonly<{
  comparison: ScoredComparison;
  onEditWeight: (factor: CompareFactor) => void;
}>;

export const CompareHeatMap = ({ comparison, onEditWeight }: CompareHeatMapProps) => {
  const topId = topMatchId(comparison.colleges);
  const isTop = (college: ComparedCollege) => college.institution_id === topId;
  const isEmpty = comparison.colleges.length === 0;

  const headers = [
    { content: STRINGS.columns.factor, sx: factorColumnSx },
    ...comparison.colleges.map((college) => ({
      content: (
        <Box sx={collegeHeaderSx}>
          <Box sx={nameLineSx}>
            <CollegeChip
              id={`compare-heat-map-college-${college.institution_id}`}
              college={college}
            />
            {college.ipeds_id !== null && (
              <AddIconChip
                id={`compare-heat-map-add-${college.institution_id}`}
                college={{ ...college, ipeds_id: college.ipeds_id, school_name: college.name }}
              />
            )}
          </Box>
          <Box sx={matchLineSx}>
            {isTop(college) && <TopMatchBadge />}
            <Typography
              id={`compare-heat-map-match-${college.institution_id}`}
              variant="text-sm-regular"
              sx={mutedSx}
            >
              {college.match === null ? STRINGS.notScored : `${percent(college.match)}%`}
            </Typography>
          </Box>
        </Box>
      ),
      sx: [collegeColumnSx, topOutline(isTop(college), 'top')] as SxProps<Theme>,
    })),
    ...(isEmpty ? [{ content: '', sx: fillerSx }] : []),
  ];

  return (
    <Box sx={scrollSx}>
      <GenericTable
        id="compare-heat-map"
        headers={headers}
        tableSx={tableSx(comparison.colleges.length)}
      >
        {COMPARE_FACTORS.map((factor) => (
          <TableRow key={factor} id={`compare-heat-map-row-${factor}`}>
            <TableCell>
              <Box sx={factorNameSx}>
                <Typography variant="text-sm-semibold" sx={mutedSx}>
                  {factorLabel(factor)}
                </Typography>
                <WeightBadge
                  factor={factor}
                  share={comparison.weights[factor]}
                  onClick={onEditWeight}
                />
              </Box>
            </TableCell>
            {comparison.colleges.map((college) => {
              const { fit } = college.factors[factor];
              const fact = factFor(college, factor);

              return (
                <TableCell
                  key={college.institution_id}
                  sx={[factCellSx(fitFill(fit)), topOutline(isTop(college))] as SxProps<Theme>}
                >
                  <Typography variant="text-sm-regular" sx={mutedSx}>
                    {fact !== null && isEstimate(college, factor) ? (
                      <EstimatedAmount amount={fact} />
                    ) : (
                      (fact ?? STRINGS.noData)
                    )}
                  </Typography>
                </TableCell>
              );
            })}
            {isEmpty && <TableCell sx={[fillerSx, emptyRowSx] as SxProps<Theme>} />}
          </TableRow>
        ))}
        {!isEmpty && (
          <TableRow id="compare-heat-map-notes-row">
            <TableCell />
            {comparison.colleges.map((college) => (
              <TableCell
                key={college.institution_id}
                id={`compare-heat-map-notes-${college.institution_id}`}
                sx={[notesCellSx, topOutline(isTop(college), 'bottom')] as SxProps<Theme>}
              >
                <BestForWatch college={college} />
              </TableCell>
            ))}
          </TableRow>
        )}
      </GenericTable>
    </Box>
  );
};

const topOutline =
  (isTop: boolean, edge?: 'top' | 'bottom'): SxProps<Theme> =>
  ({ backrs }) => {
    if (!isTop) return {};

    const line = backrs.colors['Brand/500'];
    const sides = [`inset 2px 0 0 ${line}`, `inset -2px 0 0 ${line}`];

    if (!edge) return { boxShadow: sides.join(', ') };

    const radius = backrs.radius['radius-md'];

    return {
      boxShadow: [...sides, `inset 0 ${edge === 'top' ? 2 : -2}px 0 ${line}`].join(', '),
      borderRadius: edge === 'top' ? `${radius} ${radius} 0 0` : `0 0 ${radius} ${radius}`,
    };
  };

const scrollSx: SxProps<Theme> = {
  overflowX: 'auto',
  minWidth: 0,
};

const tableSx = (collegeCount: number): SxProps<Theme> => ({
  tableLayout: 'fixed',
  borderCollapse: 'separate',
  borderSpacing: 0,
  minWidth: `${220 + collegeCount * 180}px`,
});

const factorColumnSx: SxProps<Theme> = { width: '220px' };

const collegeColumnSx: SxProps<Theme> = ({ backrs }) => ({
  textAlign: 'center',
  paddingBlock: backrs.spacing['spacing-lg'],
});

const collegeHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  minWidth: 0,
});

const nameLineSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: backrs.spacing['spacing-xs'],
  maxWidth: '100%',
  minWidth: 0,
});

const matchLineSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const factorNameSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-md'],
});

const factCellSx =
  (fill: ColorToken): SxProps<Theme> =>
  ({ backrs }) => ({
    height: '72px',
    textAlign: 'center',
    backgroundColor: backrs.colors[fill],
  });

const fillerSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  borderBottom: 'none',
});

const emptyRowSx: SxProps<Theme> = { height: '72px' };

const notesCellSx: SxProps<Theme> = ({ backrs }) => ({
  paddingBlock: backrs.spacing['spacing-xl'],
});

const mutedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
